import { useEffect } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import type { EditorView, OrderView } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { AppShell } from '@/components/app/app-shell'
import { PageEditor } from '@/components/app/editor/page-editor'
import { OrderError, startSession } from '@/components/app/order'
import { LoadingState } from '@/components/app/states'
import { buttonVariants } from '@/components/ui/button'

// The page editor full screen, outside the account layout (the `o_` prefix keeps it out of the
// /o/$orderId layout): a slim bar, the page and the side panel. "Edit your site" opens it.

export const Route = createFileRoute('/o_/$orderId/edit')({
  validateSearch: z.object({ t: z.string().optional().catch(undefined), page: z.string().max(200).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ params, deps, abortController }) => {
    if (deps.t) await startSession(params.orderId, deps.t, abortController.signal)
    const id = encodeURIComponent(params.orderId)
    const [order, view] = await Promise.all([
      api<OrderView>(`/orders/${id}`, { signal: abortController.signal }),
      api<EditorView>(`/orders/${id}/editor`, { signal: abortController.signal }),
    ])
    return { order, view }
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData ? `Edit · ${loaderData.order.businessName} · Hedgehog` : 'Edit your site · Hedgehog' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  pendingComponent: () => <LoadingState label="Opening the editor" className="h-dvh" />,
  errorComponent: ({ error }) => (
    <AppShell>
      <OrderError error={error} title="We could not open the editor" />
    </AppShell>
  ),
  component: FullScreenEditor,
})

function FullScreenEditor() {
  const { order, view } = Route.useLoaderData()
  const { t, page } = Route.useSearch()
  const navigate = useNavigate()

  // Drop the access token from the address bar once the session cookie is set.
  useEffect(() => {
    if (t) void navigate({ to: '.', search: { t: undefined, page }, replace: true })
  }, [t, page, navigate])

  if (view.status !== 'ready') {
    return (
      <AppShell>
        <div className="flex max-w-xl flex-col items-start gap-3 py-8">
          <h1 className="text-xl font-semibold">The editor is not ready yet</h1>
          <p className="text-muted-foreground">Your account shows when it is ready, and how to reach us in the meantime.</p>
          <Link to="/o/$orderId/editor" params={{ orderId: order.id }} className={buttonVariants({ variant: 'outline' })}>
            Back to your account
          </Link>
        </div>
      </AppShell>
    )
  }
  return <PageEditor orderId={order.id} businessName={order.businessName} initialPage={page} gallery={!!order.site?.features.includes('gallery')} />
}
